import { QueryClientProvider } from '@tanstack/react-query';
import {
  BootLoader,
  createAppQueryClient,
  lazyWithReload,
  MfaChallengePage,
  RequireMfaVerified,
} from '@pc/ui';
import { useTranslation } from 'react-i18next';
import {
  RouterProvider,
  createBrowserRouter,
  Navigate,
  Outlet,
  useLocation,
  useParams,
} from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Suspense, useEffect, useState } from 'react';
import type { Session } from '@pc/db';
import { getSession, getShopProduct, initSupabase, isSignOutInFlight } from '@pc/db';
import { env } from './env';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { VendorContextProvider, useVendorContext } from './auth/VendorContext';
import { AuthCallbackPage } from './auth/AuthCallbackPage';
import { RequireVendor } from './auth/RequireVendor';
import { SellShell } from './shell/SellShell';

/**
 * Where to bounce unauthenticated visitors to the root path. Deeper
 * routes (e.g. /inbox/<id>) still go to /sign-in so the user can
 * authenticate and land back on the intended page; the root is a pure
 * marketing entrypoint and belongs on the public landing page so first-
 * time visitors see the pitch before the signup form.
 */
const PUBLIC_LANDING_URL = 'https://peptideclients.com/sell.html';

// Use `lazyWithReload` instead of `React.lazy` so a stale-shell user that
// clicks a nav link after a deploy auto-recovers via a debounced page
// reload instead of hitting "Unexpected Application Error! Failed to
// fetch dynamically imported module: .../ProductsPage-<oldhash>.js".
// See packages/ui/src/lazyWithReload.ts for the full failure-mode
// writeup; the eslint rule in eslint.config.js blocks `lazy` imports in
// apps/*/src so this never regresses.
const DashboardPage = lazyWithReload(() => import('./pages/DashboardPage'));
const ProfilePage = lazyWithReload(() => import('./pages/ProfilePage'));
const ProductsPage = lazyWithReload(() => import('./pages/ProductsPage'));
const ProductEditorPage = lazyWithReload(() => import('./pages/ProductEditorPage'));
const ProductViewPage = lazyWithReload(() => import('./pages/ProductViewPage'));
const InboxPage = lazyWithReload(() => import('./pages/InboxPage'));
const AdsPage = lazyWithReload(() => import('./pages/AdsPage'));
const AdCampaignBuilderPage = lazyWithReload(
  () => import('./pages/AdCampaignBuilderPage'),
);
const AdCampaignDetailPage = lazyWithReload(
  () => import('./pages/AdCampaignDetailPage'),
);
const TeamPage = lazyWithReload(() => import('./pages/TeamPage'));
const OnboardingPage = lazyWithReload(() => import('./pages/OnboardingPage'));
const OnboardingApplicationPage = lazyWithReload(
  () => import('./pages/OnboardingApplicationPage'),
);
const OrdersPage = lazyWithReload(() => import('./pages/OrdersPage'));
const OrderDetailPage = lazyWithReload(() => import('./pages/OrderDetailPage'));
const StorefrontPickerPage = lazyWithReload(
  () => import('./pages/StorefrontPickerPage'),
);
const SellPoListPage = lazyWithReload(() => import('./pages/PoListPage'));
const SellPoDetailPage = lazyWithReload(() => import('./pages/PoDetailPage'));
const ClaimVendorPage = lazyWithReload(() => import('./pages/ClaimVendorPage'));
const ReturnsInboxPage = lazyWithReload(() => import('./pages/ReturnsInboxPage'));
const ReturnDetailPage = lazyWithReload(() => import('./pages/ReturnDetailPage'));
const ReturnsPolicyPage = lazyWithReload(() => import('./pages/ReturnsPolicyPage'));
const ProductAnalyticsPage = lazyWithReload(
  () => import('./pages/ProductAnalyticsPage'),
);
const HelpInboxPage = lazyWithReload(() => import('./pages/HelpInboxPage'));
const HelpThreadPage = lazyWithReload(() => import('./pages/HelpThreadPage'));
const NotFoundPage = lazyWithReload(() => import('./pages/NotFoundPage'));

initSupabase({
  url: env.supabaseUrl,
  anonKey: env.supabaseAnonKey,
  storageKey: 'pc-sell.auth',
});

const queryClient = createAppQueryClient();

function Loading() {
  const { t } = useTranslation();
  return <BootLoader label={t('app.loading', 'Loading')} />;
}

// Central auth: when there is no session, bounce to
// `auth.peptideclients.com/login?audience=vendor` with the current full
// URL serialized as `?return_to=`. Root path is special: anonymous
// visitors to `/` still go to the public marketing landing page so first-
// time visitors see the pitch instead of being punted to a login form.
const CENTRAL_AUTH_LOGIN_URL = 'https://auth.peptideclients.com/login';

function RequireAuth() {
  const { session, loading } = useAuth();
  const location = useLocation();
  const [resolvedSession, setResolvedSession] = useState<Session | null | undefined>(
    undefined,
  );
  // Forces a re-render after the sign-out sentinel TTL expires, in
  // case the expected `window.location.replace` to auth/signout never
  // committed (e.g. browser extension blocked it). Without this tick
  // RequireAuth would be stuck on `<Loading />` indefinitely.
  const [signOutTick, setSignOutTick] = useState(0);

  useEffect(() => {
    if (loading) return;
    if (session) {
      setResolvedSession(session);
      return;
    }
    let alive = true;
    getSession()
      .then((s) => {
        if (alive) setResolvedSession(s);
      })
      .catch(() => {
        if (alive) setResolvedSession(null);
      });
    return () => {
      alive = false;
    };
  }, [loading, session]);

  // Sign-out sentinel watchdog: if we're waiting on an in-flight
  // sign-out, schedule a re-check for after the sentinel TTL.
  useEffect(() => {
    if (resolvedSession || !isSignOutInFlight()) return;
    const t = window.setTimeout(() => setSignOutTick((n) => n + 1), 2500);
    return () => window.clearTimeout(t);
  }, [resolvedSession, signOutTick]);

  if (loading || resolvedSession === undefined) return <Loading />;
  if (!resolvedSession) {
    // Honor an in-flight sign-out: AuthProvider.signOut sets a
    // sessionStorage sentinel BEFORE awaiting the @pc/db signOut(),
    // because supabase-js's SIGNED_OUT broadcast triggers React to
    // re-render with session=null while AuthProvider.signOut is
    // still mid-flight. Without this check, the redirect below would
    // race against AuthProvider's window.location.replace to
    // auth.peptideclients.com/signout and win, sending the user to
    // /login with the central session still intact -- which then
    // re-issues a stale token via the "pre-existing session" hand-off
    // and loops the user back to /auth/callback with "Sign-in failed".
    if (isSignOutInFlight()) return <Loading />;
    if (location.pathname === '/') {
      window.location.replace(PUBLIC_LANDING_URL);
      return <Loading />;
    }
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams();
      params.set('audience', 'vendor');
      params.set('return_to', window.location.href);
      window.location.replace(`${CENTRAL_AUTH_LOGIN_URL}?${params.toString()}`);
    }
    return <Loading />;
  }
  return <Outlet />;
}

/**
 * Legacy redirect for `/products/:id/analytics`. The canonical URL is now
 * `/@<vendor-slug>/products/analytics/<product-slug>`; this component
 * resolves the UUID to a (vendor-slug, product-slug) pair (using the
 * caller's vendor memberships, so it works even if the product belongs
 * to one of their OTHER storefronts) and replaces the URL in-place. Old
 * bookmarks + emails + analytics-dashboard links keep working without
 * any operator follow-up.
 */
function ProductAnalyticsRedirect() {
  const { id } = useParams<{ id: string }>();
  const { available, ready } = useVendorContext();
  const productQ = useQuery({
    queryKey: ['shop-product', id],
    queryFn: () => getShopProduct(id!),
    enabled: !!id,
  });
  if (!id) return <Navigate to="/products" replace />;
  if (productQ.isLoading || !ready) return <Loading />;
  const product = productQ.data;
  if (!product) return <Navigate to="/products" replace />;
  const owning = available.find((m) => m.vendor.id === product.vendor_id);
  if (!owning) return <Navigate to="/products" replace />;
  return (
    <Navigate
      to={`/@${owning.vendor.slug}/products/analytics/${product.slug}`}
      replace
    />
  );
}

const router = createBrowserRouter([
  // Hand-off destination from auth.peptideclients.com. MUST live outside
  // RequireAuth (no session yet at this URL). Replaces the old
  // /sign-in and /signup local auth pages.
  { path: '/auth/callback', element: <AuthCallbackPage /> },
  // Public `/claim/:token` route: admin-issued vendor claim links land
  // here. Lives OUTSIDE RequireAuth because the token is the credential;
  // the redeem flow inside the page mints a session and then navigates
  // to /onboarding which IS auth-gated.
  {
    path: '/claim/:token',
    element: (
      <Suspense fallback={<Loading />}>
        <ClaimVendorPage />
      </Suspense>
    ),
  },
  {
    element: <RequireAuth />,
    children: [
      // /mfa lives inside RequireAuth (need a session) but OUTSIDE
      // RequireMfaVerified or we get a redirect loop. The shared
      // <MfaChallengePage /> bootstraps the OTP via user-sms-auth
      // and supports the recovery-code fallback for lost phones.
      { path: 'mfa', element: <MfaChallengePage /> },
      {
        element: <RequireMfaVerified />,
        children: [
      {
        path: 'onboarding',
        element: (
          <Suspense fallback={<Loading />}>
            <OnboardingPage />
          </Suspense>
        ),
      },
      {
        // Vendor application form for users who arrive here from
        // auth.peptideclients.com/signup?audience=vendor without an
        // existing vendor membership. Lives BEHIND RequireAuth (need a
        // session) but OUTSIDE RequireVendor (no vendor record yet).
        // RequireVendor also routes users with 0 memberships here.
        path: 'onboarding-application',
        element: (
          <Suspense fallback={<Loading />}>
            <OnboardingApplicationPage />
          </Suspense>
        ),
      },
      {
        // Picker sits inside RequireAuth (need a session) but OUTSIDE
        // RequireVendor (the whole point is to pick or create a vendor).
        // RequireVendor itself routes the user here when they have 1+
        // memberships but no active selection.
        path: 'choose-storefront',
        element: (
          <Suspense fallback={<Loading />}>
            <StorefrontPickerPage />
          </Suspense>
        ),
      },
      {
        element: <RequireVendor />,
        children: [
          {
            element: <SellShell />,
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<Loading />}>
                    <DashboardPage />
                  </Suspense>
                ),
              },
              {
                path: 'profile',
                element: (
                  <Suspense fallback={<Loading />}>
                    <ProfilePage />
                  </Suspense>
                ),
              },
              {
                path: 'products',
                element: (
                  <Suspense fallback={<Loading />}>
                    <ProductsPage />
                  </Suspense>
                ),
              },
              {
                // Create form. Lives on its own URL so the editor never
                // has to disambiguate "create" from "edit existing" via
                // a sentinel id; the route table is the single source of
                // truth. After a successful create, ProductEditorPage
                // navigates to `/products/<new-id>` (the read-only view
                // below), which is what closes the form per the
                // "create -> view" UX contract.
                path: 'products/new',
                element: (
                  <Suspense fallback={<Loading />}>
                    <ProductEditorPage />
                  </Suspense>
                ),
              },
              {
                // Read-only seller view. Mirrors the ads-builder split
                // (`/ads/:id` view vs. `/ads/:id/edit` editor) so a
                // seller landing on `/products/<id>` from the products
                // list -- or after a successful create -- sees a
                // summary of the listing they already saved instead of
                // the full edit form.
                path: 'products/:id',
                element: (
                  <Suspense fallback={<Loading />}>
                    <ProductViewPage />
                  </Suspense>
                ),
              },
              {
                // Edit form. Reached from the view page's "Edit listing"
                // button or from any "Edit product" affordance elsewhere
                // in the app.
                path: 'products/:id/edit',
                element: (
                  <Suspense fallback={<Loading />}>
                    <ProductEditorPage />
                  </Suspense>
                ),
              },
              {
                // Canonical pretty URL for the per-product analytics
                // drill-down: `/@<vendor>/products/analytics/<product>`.
                // Both segments are slugs so the address bar reads
                // human-meaningful (no raw UUIDs).
                //
                // IMPORTANT: the path captures the vendor handle WITH the
                // leading `@` as a single segment, then `ProductAnalyticsPage`
                // strips it. React Router v6's `compilePath` only recognises
                // `:name` as a dynamic segment when it sits directly after a
                // `/` (regex `\/:([\w-]+)` in @remix-run/router), so an
                // inline form like `@:vendorSlug/...` makes `:vendorSlug`
                // literal text and the route silently falls through to the
                // catch-all `*`. Same trap that bit `apps/shop` on
                // 2026-05-19 (see comment in apps/shop/src/App.tsx) and the
                // `apps/sell` analytics drill-down on 2026-05-20. ESLint
                // `no-restricted-syntax` now blocks the inline form; full
                // story in ai-rules/react-router-no-inline-at-param.md.
                path: ':vendorHandle/products/analytics/:productSlug',
                element: (
                  <Suspense fallback={<Loading />}>
                    <ProductAnalyticsPage />
                  </Suspense>
                ),
              },
              {
                // Legacy UUID URL. Resolves the id to (vendor-slug,
                // product-slug) and `replace`s the URL with the pretty
                // form. Kept so old bookmarks, emails, and the
                // analytics-dashboard "top products" links (which once
                // emitted the UUID form) keep working.
                path: 'products/:id/analytics',
                element: <ProductAnalyticsRedirect />,
              },
              {
                path: 'orders',
                element: (
                  <Suspense fallback={<Loading />}>
                    <OrdersPage />
                  </Suspense>
                ),
              },
              {
                path: 'orders/:id',
                element: (
                  <Suspense fallback={<Loading />}>
                    <OrderDetailPage />
                  </Suspense>
                ),
              },
              {
                path: 'returns',
                element: (
                  <Suspense fallback={<Loading />}>
                    <ReturnsInboxPage />
                  </Suspense>
                ),
              },
              {
                path: 'returns/settings',
                element: (
                  <Suspense fallback={<Loading />}>
                    <ReturnsPolicyPage />
                  </Suspense>
                ),
              },
              {
                path: 'returns/:id',
                element: (
                  <Suspense fallback={<Loading />}>
                    <ReturnDetailPage />
                  </Suspense>
                ),
              },
              {
                path: 'purchase-orders',
                element: (
                  <Suspense fallback={<Loading />}>
                    <SellPoListPage />
                  </Suspense>
                ),
              },
              {
                path: 'purchase-orders/:id',
                element: (
                  <Suspense fallback={<Loading />}>
                    <SellPoDetailPage />
                  </Suspense>
                ),
              },
              {
                path: 'inbox',
                element: (
                  <Suspense fallback={<Loading />}>
                    <InboxPage />
                  </Suspense>
                ),
              },
              {
                path: 'inbox/:threadId',
                element: (
                  <Suspense fallback={<Loading />}>
                    <InboxPage />
                  </Suspense>
                ),
              },
              {
                path: 'ads',
                element: (
                  <Suspense fallback={<Loading />}>
                    <AdsPage />
                  </Suspense>
                ),
              },
              {
                path: 'ads/new',
                element: (
                  <Suspense fallback={<Loading />}>
                    <AdCampaignBuilderPage />
                  </Suspense>
                ),
              },
              {
                path: 'ads/:id',
                element: (
                  <Suspense fallback={<Loading />}>
                    <AdCampaignDetailPage />
                  </Suspense>
                ),
              },
              {
                path: 'ads/:id/edit',
                element: (
                  <Suspense fallback={<Loading />}>
                    <AdCampaignBuilderPage />
                  </Suspense>
                ),
              },
              {
                path: 'team',
                element: (
                  <Suspense fallback={<Loading />}>
                    <TeamPage />
                  </Suspense>
                ),
              },
              {
                path: 'help',
                element: (
                  <Suspense fallback={<Loading />}>
                    <HelpInboxPage />
                  </Suspense>
                ),
              },
              {
                path: 'help/:id',
                element: (
                  <Suspense fallback={<Loading />}>
                    <HelpThreadPage />
                  </Suspense>
                ),
              },
            ],
          },
        ],
      },
        ],
      },
    ],
  },
  {
    // Catch-all 404. Replaces the previous `<Navigate to="/" replace />`
    // which silently bounced bad URLs to the dashboard with no
    // explanation. Now a stale or mistyped vendor link surfaces our
    // branded 404 instead of pretending nothing was wrong.
    path: '*',
    element: (
      <Suspense fallback={<Loading />}>
        <NotFoundPage />
      </Suspense>
    ),
  },
]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <VendorContextProvider>
          <RouterProvider router={router} future={{ v7_startTransition: true }} />
        </VendorContextProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
